/**
 * ZAXIA Staff Layout - Main Layout Component
 * Wraps all dashboard pages with TopBar and Sidebar
 *
 * UI-17: Navigation Role Separation + severity-driven attention signals
 * - Polls Control Tower state for system severity (normal/warning/critical)
 * - Passes severity to Sidebar for meaningful pulse behavior
 */

import { useState, useEffect, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import { TopBar } from './TopBar';
import { Sidebar } from './Sidebar';
import { deriveSystemSeverity, type SystemSeverity } from './deriveSystemSeverity';
import { fetchGuestRequests, GuestRequestCategory, GuestRequestStatus } from '../../api';
import { fetchControlTowerState } from '../services/controlTowerService';

export function StaffLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openMaintenanceCount, setOpenMaintenanceCount] = useState(0);
  const [systemSeverity, setSystemSeverity] = useState<SystemSeverity>('normal');

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  // Poll open maintenance count for sidebar badge
  const pollMaintenanceCount = useCallback(async () => {
    try {
      const all = await fetchGuestRequests();
      const open = all.filter(
        (r) =>
          r.category === GuestRequestCategory.MAINTENANCE &&
          r.status !== GuestRequestStatus.COMPLETED,
      ).length;
      setOpenMaintenanceCount(open);
    } catch {
      // fail silently — badge just won't update
    }
  }, []);

  // Poll system severity from Control Tower state
  const pollSeverity = useCallback(async () => {
    try {
      const state = await fetchControlTowerState('default');
      const severity = deriveSystemSeverity({
        pulseStatus: state.pulse.status,
        alerts: state.alerts,
        riskCount: state.guestRadar?.risks?.length ?? 0,
      });
      setSystemSeverity(severity);
    } catch {
      // fail silently — keep last known severity
    }
  }, []);

  useEffect(() => {
    pollMaintenanceCount();
    pollSeverity();
    const timer = setInterval(pollMaintenanceCount, 30_000);
    const severityTimer = setInterval(pollSeverity, 30_000);
    return () => { clearInterval(timer); clearInterval(severityTimer); };
  }, [pollMaintenanceCount, pollSeverity]);

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      {/* Top Bar */}
      <TopBar onMenuClick={toggleSidebar} />

      {/* Main Content Area */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* Mobile Overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-20 lg:hidden"
            onClick={closeSidebar}
            aria-hidden="true"
          />
        )}

        {/* Sidebar */}
        <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} openMaintenanceCount={openMaintenanceCount} systemSeverity={systemSeverity} />

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-4 md:p-6 bg-slate-850">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
